import { useState, useEffect, useRef } from 'react';
import { useFirebaseContext, useFirebaseQuotaContext } from '../context';

export default function fetchAllOnce() {
  const [loadPage, setLoadPage] = useState(true);
  const { firebase } = useFirebaseContext();
  const { setFirebaseQuota } = useFirebaseQuotaContext();

  const [lists, setLists] = useState([]);
  const [allTasks, setAllTasks] = useState([]);

  const [maxModifiedAt, setMaxModifiedAt] = useState(0);
  const isMountedRef = useRef(null);
  const [progress, setProgress] = useState(0);

  const userDB = firebase
    .firestore()
    .collection('users')
    .doc(firebase.auth().currentUser.uid);

  const findMax = (target, val) =>
    target.length
      ? target.reduce((p, c) => (p.modifiedAt > c.modifiedAt ? p : c))
          .modifiedAt
      : val;

  const initialFetch = async (target) => {
    try {
      const loop = await userDB.collection(target).get('cache');
      let temp = [],
        updateServer = false;

      if (isMountedRef.current) {
        if (loop.docs.length) {
          updateServer = true;
          temp = loop.docs.map((item) => ({
            docID: item.id,
            ...item.data(),
          }));
        } else {
          const serverLoop = await userDB.collection(target).get('server');
          if (serverLoop.docs.length)
            temp = serverLoop.docs.map((item) => ({
              docID: item.id,
              ...item.data(),
            }));
        }

        if (target == 'lists') temp.sort((a, b) => a.order - b.order);
      }
      return [updateServer, temp];
    } catch (err) {
      if (err.toString().includes('Quota exceeded'))
        setFirebaseQuota(
          'Daily document reads/writes for the App exceeded since the project currently uses Firebase(Database) free plan. Please return tomorrow. Have a great day!'
        );

      console.log(err);
    }
  };

  const updateFetch = async (target, temp, tempMaxModifiedAt) => {
    const loop = await userDB
      .collection(target)
      .orderBy('modifiedAt', 'desc')
      .where('modifiedAt', '>', tempMaxModifiedAt)
      .get('server');
    let toOrder = false;
    let toReturn = [...temp];

    if (isMountedRef.current && loop.docs.length > 0) {
      const tempLoop = loop.docs.map((item) => ({
        docID: item.id,
        ...item.data(),
      }));

      tempLoop.map((item) => {
        const index = toReturn.findIndex(
          (tempItem) => item.docID == tempItem.docID
        );
        if (index > -1) toReturn[index] = item;
        else {
          toReturn.push(item);
          toOrder = true;
        }
      });

      if (target == 'lists' && toOrder)
        toReturn.sort((a, b) => a.order - b.order);
    }

    return toReturn;
  };

  useEffect(async () => {
    isMountedRef.current = true;
    let temp,
      tempLists = [],
      tempTasks = [],
      tempMaxModifiedAt = 0;
    setProgress(5);

    temp = await initialFetch('lists');
    let updateListServer = temp[0];
    tempLists = temp[1];
    setProgress((100 / 6) * 1);

    temp = await initialFetch('tasks');
    let updateTaskServer = temp[0];
    tempTasks = temp[1];
    setProgress((100 / 6) * 2);

    if (updateListServer || updateTaskServer)
      tempMaxModifiedAt = Math.max(
        findMax(tempLists, tempMaxModifiedAt),
        findMax(tempTasks, tempMaxModifiedAt)
      );
    setProgress((100 / 6) * 3);

    if (updateListServer)
      tempLists = await updateFetch('lists', tempLists, tempMaxModifiedAt);
    setProgress((100 / 6) * 4);
    if (updateTaskServer)
      tempTasks = await updateFetch('tasks', tempTasks, tempMaxModifiedAt);
    setProgress((100 / 6) * 5);

    setLists(tempLists);
    setAllTasks(tempTasks);
    setMaxModifiedAt(
      Math.max(
        findMax(tempLists, tempMaxModifiedAt),
        findMax(tempTasks, tempMaxModifiedAt)
      )
    );
    setProgress(100);
    setLoadPage(false);

    return () => (isMountedRef.current = false);
  }, []);

  return {
    maxModifiedAt,
    setMaxModifiedAt,
    lists,
    setLists,
    allTasks,
    setAllTasks,
    loadPage,
    progress,
    setProgress,
  };
}
